import Axios from 'axios'
import { Auth } from '../service/Auth'
import store from '../store'

const isNotSet = v => v == null || v == undefined
const messages = {
  400: 'Falha na requisição! Por favor tente novamente.',
  401: 'Falha na autenticação! Por favor tente novamente',
  403: 'Você não possui permissão para este recurso.',
  404: 'Recurso não encontrado!',
  500: 'Erro Interno do Servidor!',
  default: 'Erro desconhecido durante a requisição!'
}

/**
 * Cria um cliente REST
 * @class HttpService
 */
export class RestClient {
  constructor(baseURL) {
    this.request = Axios.create({
      baseURL: [process.env.VUE_APP_API, baseURL].join('/')
    })

    this.request.interceptors.request.use(async req => {
      const token = await Auth.checkAccessToken()
      req.headers.Authorization = `Bearer ${token}`
      return req
    })

    this.request.interceptors.response.use(
      response => response,
      error => {
        if (error.request.noToast) throw error
        const { status } = error.response
        if (status in messages) {
          store.dispatch('toast', { message: messages[status] })
        } else if (status % 400 < 100) {
          store.dispatch('toast', { message: messages[400] })
        } else if (status % 500 < 100) {
          store.dispatch('toast', { message: messages[500] })
        } else {
          store.dispatch('toast', { message: messages.default })
        }
        //console.log('ERRO', error)
        throw error
      }
    )
  }

  /**
   * Lista todos os itens de um recurso
   * @param {Object} params Parâmetros de URL
   * @returns {Promise}
   */
  async findAll(params = {}) {
    const response = await this.request.get('', {
      params
    })
    return response.data
  }

  /**
   * Lista um item de um recurso
   * @param {String|number} id
   * @param {Object} params Parâmetros de URL
   * @returns {Promise}
   */
  async findById(id, params = {}) {
    if (isNotSet(id))
      throw new TypeError(
        `Nenhum ID foi passado para ${this.constructor.name}#findById().`
      )
    const response = await this.request.get(`${id}`, {
      params
    })
    return response.data
  }

  /**
   * Cria um item no recurso
   * @param {Object} data
   * @param {Object} params Parâmetros de URL
   * @returns {Promise}
   */
  async create(data, params = {}) {
    if (isNotSet(data))
      throw new TypeError(
        `Nenhum dado foi enviado para ${this.constructor.name}#create()`
      )
    delete data.id
    const response = await this.request.post('', data, {
      params
    })
    return response.data
  }

  /**
   * Atualiza um item no recurso
   * @param {Object} data Campos a serem atualizados
   * @param {string|number} data.id Id do recurso a ser atualizado
   * @param {Object} params Parâmetros de URL
   * @returns {Promise}
   */
  async update(data, params = {}) {
    if (isNotSet(data))
      throw new TypeError(
        `Nenhum dado foi passado para ${this.constructor.name}#update()`
      )
    if (isNotSet(data.id))
      throw new TypeError(
        `O campo data.id não foi passado para ${this.constructor.name}#update()`
      )
    const response = await this.request.put(`${data.id}`, data, {
      params
    })
    return response.data
  }

  /**
   * Cria ou atualiza um item no recurso
   * @param {Object.<string>} data Dados do recurso
   * @param {string|number} [data.id] Id do recurso (caso queira atualizar)
   * @param {Object} params Parâmetros de URL
   * @returns {Promise}
   */
  save(data, params = {}) {
    if (isNotSet(data))
      throw new TypeError(
        `Nenhum dado foi passado para ${this.constructor.name}#save()`
      )
    if (isNotSet(data.id)) return this.create(data, params)
    return this.update(data, params)
  }

  /**
   * Exclui um recurso
   * @param {string|number} id
   * @param {Object} params Parâmetros de URL
   * @returns {Promise}
   */
  async delete(id, params = {}) {
    if (isNotSet(id))
      throw new TypeError(
        `ID não foi passado para ${this.constructor.name}#delete()`
      )
    const response = await this.request.delete(`${id}`, {
      params
    })
    return response.data
  }

  /**
   * Filtrar lista
   * @param {*} params
   */
  async filter(params = {}) {
    const response = await this.request.get('/filtrar', {
      params
    })
    return response.data
  }
}
